import { Stack, Typography, Markdown } from "@altimateai/lego";
import { Chart, ChartTypes } from "@uicore";
import { Area, Bar, Line, ReferenceLine } from "recharts";
import { ReactNode } from "react";
import { useSubscriptionPreview } from "./services/wizardApi";
import type { PreviewItem, SubscriptionPreviewParams } from "./types";
import { PreviewEmptyState, PreviewErrorState, PreviewLoadingState, PreviewShell } from "./preview";
import { ChangeType } from "@/pages/subscriptions/constants";

interface SubscriptionPreviewProps {
  previewParams?: SubscriptionPreviewParams;
  enabled?: boolean;
  metricUnit?: string;
  actions?: ReactNode;
}

const SubscriptionPreview = ({
  previewParams,
  enabled = true,
  metricUnit,
  actions,
}: SubscriptionPreviewProps) => {
  // Check if we have a metric selected (for both alerts and reports)
  const hasMetricSelected = Boolean(previewParams?.metric_config?.metric_name);

  // Only fetch preview when enabled (on CUSTOMIZE step) AND metric is selected
  const shouldFetchPreview = enabled && hasMetricSelected;

  const {
    data: previewData,
    isLoading,
    isError,
    refetch,
  } = useSubscriptionPreview(previewParams, shouldFetchPreview);

  // Show empty state when not on customize step yet, or no metric selected
  if (!enabled || !previewParams || !hasMetricSelected) {
    return <PreviewEmptyState />;
  }

  if (isLoading) {
    return <PreviewLoadingState />;
  }

  if (isError || !previewData) {
    return <PreviewErrorState onRetry={() => refetch()} />;
  }

  const changeType = previewParams?.metric_config?.params?.comparison_type;
  const thresholdValue = previewParams?.metric_config?.params?.threshold_value;
  const isPercentage = previewParams?.metric_config?.params?.is_percentage;
  // Only show threshold line for absolute_value comparisons with dollar amounts
  // For relative_value, the threshold represents the DIFFERENCE between periods, not an absolute value
  const showThresholdLine =
    changeType === ChangeType.ABSOLUTE_VALUE && typeof thresholdValue === "number" && !isPercentage;

  const renderPreviewItem = (item: PreviewItem, index: number) => {
    if (item.type === "markdown" && item.content) {
      return (
        <div key={index} className="al-prose al-prose-sm al-max-w-none">
          <Markdown content={item.content} />
        </div>
      );
    }

    if (item.type === "chart" && item.chart) {
      const { chart } = item;

      // Determine chart type
      let chartType = ChartTypes.BarChart;
      if (chart.type === "line") {
        chartType = ChartTypes.LineChart;
      } else if (chart.type === "area") {
        chartType = ChartTypes.AreaChart;
      }

      // Handle multi-series data for line charts
      const isMultiSeries = chart.data.length > 0 && Object.keys(chart.data[0]).length > 2;

      const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

      // Render chart series based on type
      const renderChartSeries = (barSize: number, selectedLegend?: string) => {
        if (isMultiSeries) {
          const keys = Object.keys(chart.data[0]).filter(key => key !== chart.x_key);

          if (chart.type === "line") {
            return keys.map((key, idx) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[idx % colors.length]}
                fill={colors[idx % colors.length]}
                strokeWidth={2}
                hide={Boolean(selectedLegend && selectedLegend !== key)}
              />
            ));
          } else if (chart.type === "area") {
            return keys.map((key, idx) => (
              <Area
                aria-label="area chart"
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[idx % colors.length]}
                fill={colors[idx % colors.length]}
                strokeWidth={2}
                hide={Boolean(selectedLegend && selectedLegend !== key)}
              />
            ));
          } else {
            return keys.map((key, idx) => (
              <Bar
                key={key}
                barSize={barSize}
                dataKey={key}
                fill={colors[idx % colors.length]}
                hide={Boolean(selectedLegend && selectedLegend !== key)}
              />
            ));
          }
        } else {
          // Single series
          if (chart.type === "line") {
            return (
              <Line
                type="monotone"
                dataKey={chart.y_key}
                stroke="#3b82f6"
                fill="#3b82f6"
                strokeWidth={2}
              />
            );
          } else if (chart.type === "area") {
            return (
              <Area
                aria-label="area chart"
                type="monotone"
                dataKey={chart.y_key}
                stroke="#3b82f6"
                fill="#3b82f6"
                strokeWidth={2}
              />
            );
          } else {
            return <Bar barSize={barSize} dataKey={chart.y_key} fill="#3b82f6" />;
          }
        }
      };

      return (
        <div key={index} className="al-w-full">
          {chart.title && (
            <Typography variant="body" weight="medium" className="al-mb-4 al-text-gray-900">
              {chart.title}
            </Typography>
          )}
          <div className="al-w-full">
            <Chart
              type={chartType}
              data={chart.data}
              xAxisDataKey={chart.x_key}
              title={chart.title || ""}
              yAxisLabel={{ value: chart.y_label || "", angle: -90, position: "insideLeft" }}
            >
              {({ barSize, selectedLegend }) => (
                <>
                  {renderChartSeries(barSize, selectedLegend)}
                  {showThresholdLine && (
                    <ReferenceLine
                      y={thresholdValue}
                      stroke="#ef4444"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      ifOverflow="extendDomain"
                      label={{
                        value: `${metricUnit || ""} ${thresholdValue?.toLocaleString()}`,
                        position: "insideTopRight",
                        fill: "#ef4444",
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    />
                  )}
                </>
              )}
            </Chart>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <PreviewShell>
      <Stack flex="1" direction="column" className="al-pr-2 al-overflow-y-auto">
        <Stack direction="column" gap="xl">
          {previewData.items.map((item, index) => renderPreviewItem(item, index))}
        </Stack>
      </Stack>
      {actions ? (
        <Stack className="al-pt-4" align="end" justify="end">
          {actions}
        </Stack>
      ) : null}
    </PreviewShell>
  );
};

export default SubscriptionPreview;
